import { useMemo, type AllHTMLAttributes, type ReactNode } from "react";

import { DrawerBody } from "./drawer-body/drawer-body.component";
import { DrawerContent } from "./drawer-content/drawer-content.component";
import { DrawerFooter } from "./drawer-footer/drawer-footer.component";
import { DrawerHeader } from "./drawer-header/drawer-header.component";
import { useDrawer } from "./hooks";
import { DrawerProvider } from "./providers/drawer.provider";

import styles from "./drawer.module.css";

export type DrawerProps = {
  children: ReactNode;
  name: string;
  anchor?: "top" | "bottom" | "right" | "left";
} & AllHTMLAttributes<HTMLDivElement>;

export function Drawer({
  anchor = "bottom",
  name,
  children,
  className = "",
  ...props
}: DrawerProps) {
  const { isOpen } = useDrawer();

  const classNameMemo = useMemo(() => {
    if (isOpen(name)) {
      return `${styles.Container} ${styles.Open} ${styles[anchor]} ${className}`;
    }

    return `${styles.Container} ${styles.Closed} ${styles[anchor]} ${className}`;
  }, [isOpen, className, name, anchor]);

  return (
    <div data-name={name} className={classNameMemo} {...props}>
      {children}
    </div>
  );
}

export namespace Drawer {
  export const Provider = DrawerProvider;
  export const Content = DrawerContent;
  export const Footer = DrawerFooter;
  export const Header = DrawerHeader;
  export const Body = DrawerBody;
}
